/**
 * A gaveta em duas linhas: cliente, e abaixo dele as soluções que formam o
 * valor dele.
 *
 * A lista plana repetia o nome do cliente uma vez por solução, e num
 * detalhamento de EP com dois ou três produtos por pessoa isso vira uma coluna
 * de nomes repetidos que esconde a informação nova — que é a solução.
 *
 * AGRUPA O QUE ESTÁ NA PÁGINA, E SÓ.
 *
 * A gaveta pagina em 25 itens, então um cliente com muitas soluções pode ficar
 * partido entre páginas. O subtotal do cliente é, portanto, a soma do que está
 * LISTADO abaixo dele — não o total histórico daquele cliente. Quem lê precisa
 * saber disso, e é por isso que o cabeçalho da coluna diz "nesta página" em vez
 * de "total": um número que parece total sem ser é pior do que número nenhum.
 *
 * A ordem é a de chegada: o backend já ordena, e reordenar aqui faria a
 * paginação parecer embaralhada entre uma página e outra.
 */

const toNumber = valor => {
  const numero = Number(valor)
  return Number.isFinite(numero) ? numero : 0
}

/**
 * @returns {Array<{customerId: *, itens: Array, total: number, quantos: number}>}
 */
const groupByCustomer = (items = []) => {
  const porCliente = new Map()

  const lista = items || []

  lista.forEach(item => {
    // `null` e `undefined` viram a mesma chave de propósito: item sem cliente é
    // um caso só ("sem cliente"), e não um por linha.
    const chave = item.customer_id === undefined ? null : item.customer_id

    if (!porCliente.has(chave)) {
      porCliente.set(chave, {
        customerId: chave, itens: [], total: 0, quantos: 0,
      })
    }

    const grupo = porCliente.get(chave)
    grupo.itens.push(item)
    grupo.total += toNumber(item.value)
    grupo.quantos += 1
  })

  return [...porCliente.values()]
}

/** O executor aparece como anotação sob a linha do DONO, nunca como outro total. */
const executorDoItem = item => {
  if (!item || !item.specialist_consultant_id) {
    return null
  }

  return item.specialist_consultant_id
}

/**
 * A gaveta confere com a CÉLULA CLICADA, e não consigo mesma.
 *
 * O backend devolve `cell_amount` (o agregado que ELE recalcula) e
 * `reconciles` (esse agregado contra a lista que ELE montou). Os dois lados da
 * conta eram dele, então qualquer diferença entre a leitura da tabela e a da
 * gaveta passava como "confere". Foi assim com o filtro de Solução: a célula
 * mostrava R$ 2.860,00 (só EP) e a gaveta "R$ 33.163,42 ✓ confere" — tudo do
 * consultor, sem filtro.
 *
 * A referência é sempre o que a pessoa clicou, na MESMA UNIDADE da célula:
 *
 * - DINHEIRO: a soma dos itens tem de fechar com o valor. A tolerância é a do
 *   backend — meio centavo por item, nunca menos de um centavo —, porque o
 *   agregado soma e arredonda e a lista arredonda item a item.
 * - CONTAGEM: o número de itens listados tem de ser a contagem. Comparar a
 *   contagem com dinheiro era o outro defeito: "EP 1" abria dizendo "Valor da
 *   célula R$ 1,00" contra R$ 11.440,26 listados. Medido na leitura de dono, em
 *   set/2026: EP 52/52, Suporte 18/18, Acompanhamentos 41/41 e Renovações
 *   103/103 células com o número de itens igual à contagem.
 *
 * `unidade` diz à gaveta como mostrar os dois lados da conta.
 */
const conferirComACelula = (meta, valorDaCelula, tipoDaColuna) => {
  const celula = Number(valorDaCelula)
  const comparavel = tipoDaColuna === 'money' || tipoDaColuna === 'int'
  if (!meta || !comparavel || valorDaCelula === null || valorDaCelula === undefined || !Number.isFinite(celula)) {
    return meta
  }

  if (tipoDaColuna === 'int') {
    return {
      ...meta,
      unidade: 'contagem',
      cell_amount: celula,
      reconciles: Math.round(celula) === Math.round(toNumber(meta.total)),
    }
  }

  const somaDosItens = toNumber(meta.items_amount)
  const tolerancia = Math.max(0.01, 0.005 * Math.max(1, toNumber(meta.total)))
  const diferenca = Math.round(Math.abs(celula - somaDosItens) * 100) / 100

  return {
    ...meta,
    unidade: 'dinheiro',
    cell_amount: celula,
    reconciles: diferenca <= tolerancia,
  }
}

module.exports = {
  groupByCustomer,
  executorDoItem,
  conferirComACelula,
}
